// client/src/features/quotations/itemLookupRows.ts
//
// THE ONE PLACE THAT ANSWERS: "what does this row cost at the panel's chosen
// unit, and if it cannot be stated, why not."
//
// `ItemLookupPanel` merges two lookups that used to be separate popups —
// `MasterCatalogueSearchPopup` (Master Catalogue rows, a per-row unit picker)
// and `SupplierDropdown` (vendor-invoice / historical-quotation / price-list
// rows, priced per the line's own unit by the server). This module is the
// pure core both row kinds go through, so the two cannot drift the way the
// two popups' independent unit arithmetic already had: a catalogue row and a
// supplier row for the same product could show a "cheaper" price that was
// actually stated per a different unit.
//
// No React, no fetching — everything here is deterministic given the data the
// component already fetched. Every number comes from an OWNER already in this
// codebase; this file's only job is to hand each row to the right one and
// turn a missing answer into a refusal, never a guess (the ~6.8x TT$-read-as-
// US$ error TECH_DEBT #152 is the shape every refusal below exists to stop).

import type { MasterCatalogueItem } from '../../lib/masterCatalogueApi';
import type { SupplierDropdownItem } from '../../lib/vendorInvoiceApi';
import {
  costUnitOptions,
  costUnitMoneyFromTtdWithVat,
  costUnitMoneyFromUsd,
  shownByUnit,
  catalogueRowIsTtd,
  catalogueRowNeedsUnsetVatRate,
  type CostUnitOption,
  type AltUnitPricesUsd,
} from '../catalogue/costPriceTiers';
import { getAlternateQuotationPricing } from '../../utils/quotationUnitConversions';
import { isSameUnit, canonicalUnit } from '../../utils/unitVocabulary';
import { supplierOptionRefusal } from './supplierOptionPrice';
// The identity a supplier pick is resolved by, server-side
// (`supplierOptions.optionKey`) — the same key `isPricedRow` below uses to
// tell one supplier row from another sharing its description, one owner.
import { supplierOptionIdentityKey } from '../../utils/supplierOptionIdentity';
// The catalogue/supplier tie-break: whether the line's CURRENT price came
// from a listed-option pick. One owner with the server's
// `quotationLineCatalogueOptions.pricedFromFacts`, which needs the identical
// answer for its own "how was this priced" attribution.
import { pickedAsSupplierRow } from '../../utils/quotationLinePricingFacts';
// THE conversion a supplier option's stated price becomes on a quotation line
// (BUSINESS_RULES#usd-cost-and-ttd-display) — the same call
// `QuotationDetailPage.handleSupplierSelect` and its `usdOf` make before this
// panel existed. Never a second copy: a TT$ price applied as if it were US$
// is the exact defect TECH_DEBT #152 is named for.
import { usdCostFacts } from '../../utils/fxDisplay';

/**
 * The unit price the user picked to feed into the RFQ/quote cost price field.
 *
 * Originally defined in `components/MasterCatalogueSearchPopup.tsx`. Moved
 * here when that popup (and `components/SupplierDropdown.tsx` beside it) was
 * deleted in the lookup-panel merge (2026-09-23) — `priceAtUnit` below is now
 * the only builder of a value of this shape, so this module is its home.
 */
export interface CataloguePriceSelection {
  /**
   * One of `CostUnit`'s six fixed tiers, OR — since 2026-09-24 — `unit:<UOM>`
   * for a unit `costUnitOptions`'s fixed six do not name, read off
   * `priceLevels.byUnit` (see `catalogueCostUnitOptions`). Widened from
   * `CostUnit` to `string` for that reason. The one caller that switches on
   * this (`QuotationDetailPage`'s `unitKey === 'pack'` check) is unaffected: a
   * `unit:` value can never equal the literal `'pack'`.
   */
  unitKey: string;
  label: string;
  /**
   * Per selected unit, VAT-INCLUSIVE, USD (quote-internal currency) — the
   * final price USC pays, input VAT included (owner rule, Deborah's VAT
   * report, 2026-09-24: input VAT is cost). Same figure as `unitPriceTtdWithVat`,
   * converted.
   */
  unitPriceUsd: number;
  /** The same, unrounded — the line's conversion anchor (`CostUnitOption.usdExact`). */
  unitPriceUsdExact: number;
  /** Per selected unit, VAT-inclusive, TT$ (what the user saw). */
  unitPriceTtdWithVat: number;
  /**
   * Per selected unit, VAT-INCLUSIVE, TT$ — and null unless the catalogue row
   * states its price in TT$. The SAME figure as `unitPriceTtdWithVat`
   * (`CostUnitOption.ttdCost` is no longer a VAT-exclusive derivation of
   * `ttdWithVat` — see that field's doc comment in `costPriceTiers.ts`), kept
   * as a separate field only because a US$-priced row states neither figure.
   *
   * The line pins this as `unitPriceTtd`, exactly as a generated line pins the
   * TT$ its reference price was stated in. Without the pin the line's TT$ cost
   * is derived back out of the stored US$ cent and lands a cent or two off the
   * catalogue's own figure — TT$345.00 redisplaying as TT$345.02 — which is
   * the drift `utils/pricing.unitCostTtd` exists to stop.
   */
  unitPriceTtdCost: number | null;
  uom: string;
}

/** One row of the merged panel — a Master Catalogue pick, or a supplier option. */
export type LookupRow =
  | { kind: 'catalogue'; id: string; item: MasterCatalogueItem }
  | { kind: 'supplier'; id: string; option: SupplierDropdownItem };

/**
 * What one row costs at the panel's chosen unit, or why it cannot be stated.
 *
 * `catalogueSelection` is present only for a catalogue row's `ok: true`
 * result, and is exactly the shape the now-deleted
 * `MasterCatalogueSearchPopup.selectionFor` used to build — a click hands it
 * straight to `onSelectCatalogue`, unchanged, so a catalogue pick still means
 * the same thing it always did.
 */
export type PriceAtUnitResult =
  | {
      ok: true;
      /**
       * US$, unrounded — a conversion ANCHOR, never a number to print.
       *
       * Catalogue: `CostUnitOption.usdExact`, genuinely unrounded (the same
       * anchor a picked line seeds `unitPriceUsdExact` with — see
       * `CataloguePriceSelection`). Supplier: `usdCostFacts` is the one owner of
       * a supplier price's US$ figure (D85.6): `usdExact` the unrounded conversion,
       * `usd` a TT$ price ceiled once — see the comment at the supplier `return`
       * below.
       */
      usdExact: number;
      /** US$, the comparable figure the panel sorts and flags outliers on. */
      usd: number;
      /** TT$, and null when this row states no TT$ figure at this unit — never derived. */
      ttdWithVat: number | null;
      /**
       * TT$, VAT-INCLUSIVE — the price a line is actually costed at — and
       * null on the same terms as `ttdWithVat`. Catalogue and supplier rows
       * now agree here (owner rule, Deborah's VAT report, 2026-09-24: input
       * VAT is cost); see `CostUnitOption.ttdCost`'s doc comment for the
       * catalogue side and the supplier-row comment below for its own.
       */
      ttdCost: number | null;
      /** The unit this price is per, restated in the row's own spelling. */
      uom: string;
      /** A catalogue pick only — the exact payload `onSelectCatalogue` takes. */
      catalogueSelection?: CataloguePriceSelection;
    }
  | {
      ok: false;
      reason: string;
      /**
       * True only for a catalogue row that states NO price to cost a line
       * from at ANY unit (`catalogueCostUnitOptions` returns `[]`) — never
       * for a row that has a price but not at the chosen unit. R12: the panel
       * used to print a bare "—" for both, the same as a row genuinely priced
       * but at another unit; only THIS case is the same fact a priceless
       * supplier row's "no price" already states, so only this case reads
       * that way too. Undefined (never false) when the row is a supplier row,
       * or is refused for any other reason — a caller checks this field
       * rather than matching on `reason`'s prose (invariant 6's "never
       * string-match a message", applied to this module's own refusals).
       */
      noPrice?: boolean;
    };

/**
 * The `alt` argument `costUnitOptions` takes for one catalogue row —
 * reproduced field-for-field from `MasterCatalogueSearchPopup.unitOptionsFor`,
 * which builds it as a closure and so cannot be imported directly. Exported so
 * a test can hand this straight to `costUnitOptions` (the owner) and compare
 * against ITS OWN output, rather than trust a number only this module ever
 * computed.
 *
 * USD-native and last-resort: it only feeds a tier the row states nothing
 * else for. See `costPriceTiers.costUnitOptions`'s own header.
 */
export function catalogueAltPricing(
  item: MasterCatalogueItem,
  quotationUsdToTtd: number | null
): (AltUnitPricesUsd & { innerUnitLabel?: string | null }) | null {
  const pricingDetails =
    item.meta_data && typeof item.meta_data === 'object'
      ? (item.meta_data.pricingDetails as Record<string, unknown> | undefined)
      : undefined;
  const baseUsd =
    item.item_price != null &&
    Number.isFinite(item.item_price) &&
    quotationUsdToTtd != null &&
    quotationUsdToTtd > 0
      ? catalogueRowIsTtd(item)
        ? item.item_price / quotationUsdToTtd
        : item.item_price
      : null;
  return baseUsd == null
    ? null
    : getAlternateQuotationPricing(baseUsd, item.unit || 'pcs', item.item_name || '', pricingDetails);
}

/**
 * A `CostUnitOption`-shaped entry for a unit `costUnitOptions`'s fixed six
 * tiers do not name — read off `priceLevels.byUnit`, every OTHER unit the
 * item's own `unitConversions` reach (`catalogueCostUnitOptions` below).
 * `key` is a `unit:<UOM>` string, never one of `CostUnit`'s six literals —
 * see `CataloguePriceSelection.unitKey`'s own doc comment for why that is
 * safe for this shape's one consumer that switches on it.
 */
export interface GraphCostUnitOption extends Omit<CostUnitOption, 'key'> {
  key: string;
}

/**
 * THE REAL UNIT `costUnitOptions`'s FIXED 'pack' TIER PRICES — never the
 * literal string 'pack', which names no unit on the row at all.
 *
 * That tier is the case price divided by the row's own count of CONTENTS
 * (`packFactsFor`'s `packCount`, the server's `uomMeta.level1PerLevel2`), so
 * what it states is the price of ONE CONTENTS unit — named by
 * `uomMeta.level1Name` (`unitConversions.packedUnit`'s answer to "which unit
 * is the item's own Unit counted IN", server-side: PCS for "1 CASE = 12 PCS",
 * BTL for "1 CASE = 24 BTL", written alongside `level1PerLevel2` on every
 * save that states one). "pack" is not that unit's name; it is this
 * function's own placeholder from before a row's real count-unit was ever
 * recorded, and `costUnitOptions` (which ~40 OTHER readers depend on, and
 * which the Master Catalogue grid's own always-"Per Pack" column is happy to
 * keep calling that) is not the place to change it — see this module's own
 * header on strangling versus rewriting.
 *
 * Null keeps the literal 'pack' label exactly as `costUnitOptions` already
 * states it: a row with no `uomMeta` (written before it existed), one whose
 * stated count-unit name is not one this vocabulary knows, OR — owner
 * re-audit, 2026-09-25 — one whose `level1Name` names the SAME unit as the
 * row's own `unit`. The server writes `level1Name == unit` in more than one
 * place: `masterCatalogueImport.service.js`'s `resolveCountUnitName` answers
 * the row's own count unit back for a row already counted in it,
 * `masterCatalogueItem.service.js`'s `uomMetaFromItemFields` falls back to
 * `unit` when nothing else names a count unit, and an older vendor-invoice
 * row can carry `level1Name` written as a plain copy of `unit` ("Case").
 * Relabelling the fixed tier to THAT name does not add a real fact: it
 * prints the row's own unit a second time and, worse, makes `catalogueCost
 * UnitOptions`'s de-dup (`isSameUnit` against the fixed six) throw the
 * relabelled entry away as a "duplicate" of the real `case` tier — the
 * per-piece price this tier actually states vanishes, and the panel is left
 * offering "Per CASE" twice for one row (a CASE row, TT$120, packCount 12,
 * level1Name 'CASE': the probe this fixes). Only a `level1Name` naming a
 * genuinely DIFFERENT unit from the row's own is the real count-unit this
 * relabel exists for.
 */
function realPackUnit(item: MasterCatalogueItem): string | null {
  const raw = item.uomMeta?.level1Name;
  if (!raw) return null;
  if (isSameUnit(raw, item.unit || '')) return null;
  return canonicalUnit(raw);
}

/**
 * ONE `priceLevels.byUnit` ENTRY's money, at THIS quotation's own rate.
 *
 * `computeGraphPriceLevels` (server) normalises the row's OWN stated price to
 * TT$ through the FX rate CURRENT WHEN THE ROW WAS SAVED, walks the graph in
 * that currency, and stores BOTH a TT$ and a US$ figure at every unit — but
 * only ONE of the two is the row's genuine, currency-native money; the other
 * is that same figure pushed through the save-time rate, and which is which
 * depends on what currency the ROW ITSELF prices in (`catalogueRowIsTtd`):
 *
 *   - a TT$ row: `normalizeAmountToTtd` never multiplies a TTD amount by the
 *     fx rate, so `ttAmount` is the item's own stated money — genuinely
 *     rate-independent — and `usdAmount` (`ttRaw / fx`) is the one the
 *     save-time rate is baked into.
 *   - a US$ row: the fx rate multiplies the amount going INTO `ttAmount`
 *     (`amount * fx * uplift`) and divides it straight back OUT for
 *     `usdAmount` (`ttRaw / fx`) — the two `fx`s cancel, so `usdAmount` is
 *     the item's own stated money and `ttAmount` (which never divides back
 *     out) is the one baked to the save-time rate.
 *
 * Reading the baked-in figure and converting IT through this quotation's own
 * rate compounds two different rates into one number — TECH_DEBT #152's
 * shape, on a US$ row specifically: row-US$ x fx_saved / fx_quote instead of
 * row-US$ x fx_quote. (A prior version of this function's caller called
 * `ttAmount` "a real, currency-native TT$ price, rate-independent" with no
 * qualifier — true for a TT$ row, never exercised against a US$ one, and
 * wrong for it; see this file's own test for the repro, a US$ row saved at
 * 6.5 and quoted at 6.8.)
 *
 * Either way, the genuine figure is handed to whichever OWNER converts it
 * through THIS quotation's own rate exactly once, at the quotation's own
 * rate (invariant 5) — `costUnitMoneyFromTtdWithVat` for the TT$ side,
 * `costUnitMoneyFromUsd` for the US$ side, the same pair `costPriceTiers.ts`
 * already exports for its own fixed six tiers. This function decides WHICH
 * figure is genuine and refuses when neither is (null when the entry states
 * no usable figure on the side that matters — never read the OTHER side as a
 * fallback, that is exactly the wrong-rate figure this function exists to
 * refuse); it performs no money arithmetic of its own.
 */
function graphUnitMoney(
  entry: { ttAmount?: number | null; usdAmount?: number | null },
  opts: { isTtd: boolean; usdToTtd: number }
): { ttdWithVat: number; ttdCost: number | null; usd: number; usdExact: number } | null {
  if (opts.isTtd) {
    const ttdWithVat = entry.ttAmount;
    if (typeof ttdWithVat !== 'number' || !Number.isFinite(ttdWithVat) || ttdWithVat <= 0) return null;
    return costUnitMoneyFromTtdWithVat(ttdWithVat, opts);
  }
  const usdExact = entry.usdAmount;
  if (typeof usdExact !== 'number' || !Number.isFinite(usdExact) || usdExact <= 0) return null;
  return costUnitMoneyFromUsd(usdExact, opts);
}

/**
 * The units ONE catalogue row can honestly be priced at, and what each
 * costs — `costUnitOptions`'s fixed six tiers (case/pack/kg/lb/l/gal), PLUS
 * every OTHER unit the row's own `unitConversions` reach, read off
 * `priceLevels.byUnit` (`costEngine.computeGraphPriceLevels`, already
 * computed server-side and already rendered by the catalogue item form's
 * "Priced at, as last saved" footer — `CatalogueItemForm.tsx`'s
 * `storedByUnit`).
 *
 * Owner, 2026-09-24: "1 CASE = 12 PCS, 1 PCS = 500 ML, 1 PACK = 2 CASE" must
 * offer CASE, PCS, PACK, ML, L and GAL on a quotation, the same set the
 * catalogue screen already prices this row at — the fixed six alone would
 * offer CASE, l and gal (a PCS-per-case row derives no per-kg/per-lb figure)
 * and miss PCS, ML and the item's own PACK unit entirely. This DOES now offer
 * PACK: `costUnitOptions`'s fixed 'pack' tier is relabelled to the row's real
 * count-unit first (`realPackUnit`, e.g. PCS) — the fixed six's OWN uom used
 * to be the literal string 'pack' unconditionally, which matched `byUnit`'s
 * real PACK entry by `isSameUnit` and threw it away as a "duplicate" of a
 * figure that was not the same fact at all (F2: the panel showed "Per Pack"
 * at the one-piece price, a second "Per PCS" at the same figure, and no PACK
 * option whatsoever).
 *
 * `byUnit` is VAT-inclusive and computed at the FX rate that was current when
 * the item was last saved — never this quotation's own locked rate.
 * `graphUnitMoney` reads whichever of `ttAmount`/`usdAmount` is this row's own
 * genuine, currency-native figure (never the other one, which bakes in that
 * save-time rate — see its own header) and converts it through THIS
 * quotation's `quotationUsdToTtd` exactly once (invariant 5).
 *
 * A unit `costUnitOptions` (as relabelled above) ALREADY offers is skipped:
 * the fixed six own that unit's figure, and this only ADDS units nothing else
 * names — never a second, independently-derived number for one a reader
 * already trusts. Both sides derive from the same server-recorded pack facts
 * for a normal row, so they are not expected to disagree; this module has no
 * live-database read to confirm that holds on every stored row, so a caller
 * who finds one where they do not agree should report it rather than picking
 * a side here.
 *
 * @param vatRatePercent — the Settings VAT rate (`getVatRate`), percent, or
 *   null while it has not loaded / has never been set. Passed straight to the
 *   owner (`costPriceTiers.costUnitOptions`) — a `vat_applicable` row offers
 *   no tier at all while this is null, same refusal as a missing exchange rate.
 *   The `byUnit` extras follow the same refusal: their `ttAmount` is grossed at
 *   the Settings rate (restated server-side whenever it changes), so with no
 *   rate set a VAT row has no honest figure there either.
 */
export function catalogueCostUnitOptions(
  item: MasterCatalogueItem,
  quotationUsdToTtd: number | null,
  vatRatePercent: number | null
): (CostUnitOption | GraphCostUnitOption)[] {
  const rawFixed = costUnitOptions(
    item,
    quotationUsdToTtd,
    catalogueAltPricing(item, quotationUsdToTtd),
    vatRatePercent
  );
  if (quotationUsdToTtd == null || !Number.isFinite(quotationUsdToTtd) || quotationUsdToTtd <= 0) {
    return rawFixed;
  }
  // Reads the OWNER's own test (`costPriceTiersTtd` refuses every one of its
  // six tiers on this same condition, so `rawFixed` is already `[]` here) —
  // never a second, independently-typed copy of the boolean. Still needed as
  // its own check: without it the `byUnit` extras below would still be added
  // from a stored, VAT-inclusive-at-the-OLD-rate figure while `rawFixed` is
  // empty for the right reason.
  if (catalogueRowNeedsUnsetVatRate(item, vatRatePercent)) {
    return rawFixed;
  }

  // See `realPackUnit`. Only when the row's real count-unit is BOTH known and
  // not itself literally PACK — a row whose count-unit genuinely IS named
  // PACK states one real fact under that name, and the generic label already
  // says it correctly.
  const packUnit = realPackUnit(item);
  const fixed =
    packUnit && packUnit !== 'PACK'
      ? rawFixed.map((o) => {
          if (o.key !== 'pack') return o;
          // N10: `costUnitOptions`'s own `offer('pack', ...)` appends the
          // item-NAME reading's inner-unit hint in parentheses whenever
          // `packBasis === 'item_name'` (e.g. "Per Pack (500g unit)") — see
          // its own header. That hint describes what basis produced the
          // figure and survives the relabel; only the placeholder word
          // 'Pack' is being replaced with the row's real count-unit.
          const suffixMatch = /^Per Pack(\s*\(.+\))$/.exec(o.label);
          return { ...o, uom: packUnit, label: suffixMatch ? `Per ${packUnit}${suffixMatch[1]}` : `Per ${packUnit}` };
        })
      : rawFixed;

  const byUnit = shownByUnit(item);
  if (!Array.isArray(byUnit) || byUnit.length === 0) return fixed;

  const isTtd = catalogueRowIsTtd(item);
  const extra: GraphCostUnitOption[] = [];
  for (const entry of byUnit) {
    const unit = String(entry?.unit || '').trim();
    if (!unit) continue;
    if (fixed.some((o) => isSameUnit(o.uom, unit))) continue;
    if (extra.some((o) => isSameUnit(o.uom, unit))) continue;
    const money = graphUnitMoney(entry, { isTtd, usdToTtd: quotationUsdToTtd });
    if (!money) continue;
    extra.push({ key: `unit:${unit}`, label: `Per ${unit}`, uom: unit, ...money });
  }
  return [...fixed, ...extra];
}

/**
 * The units the panel offers in its single selector, as UOM strings — the
 * line's own unit first (the only comparison that means anything: the line is
 * quoted per it), then every unit a catalogue row on screen can honestly be
 * priced at — `catalogueCostUnitOptions`'s fixed six tiers AND every other
 * unit the row's own conversions reach (2026-09-24) — deduplicated through
 * `isSameUnit` (the unit-identity owner — "CS" and "CASE" are one choice, not
 * two) so the list never offers the same unit twice under two spellings.
 *
 * Supplier rows contribute NOTHING here: a supplier option's price is stated
 * per whatever unit the server was ASKED for (`lineUom`), never per a unit of
 * its own choosing, so there is nothing on a supplier row to offer as a new
 * choice — offering one would invite picking a unit no supplier row can then
 * be refetched for.
 *
 * A line with no unit falls back to the catalogue rows' units alone, which is
 * exactly what this returns when `lineUom` is empty: the "add the line's unit
 * first" step is then a no-op.
 */
export function lookupUnitChoices(args: {
  lineUom?: string | null;
  catalogueItems: MasterCatalogueItem[];
  quotationUsdToTtd: number | null;
  /** The Settings VAT rate (`getVatRate`), percent, or null. See `catalogueCostUnitOptions`. */
  vatRatePercent: number | null;
}): string[] {
  const { lineUom, catalogueItems, quotationUsdToTtd, vatRatePercent } = args;
  const out: string[] = [];

  const add = (u: string | null | undefined) => {
    const trimmed = String(u || '').trim();
    if (!trimmed) return;
    if (out.some((existing) => isSameUnit(existing, trimmed))) return;
    out.push(trimmed);
  };

  add(lineUom);
  for (const item of catalogueItems) {
    for (const opt of catalogueCostUnitOptions(item, quotationUsdToTtd, vatRatePercent)) {
      add(opt.uom);
    }
  }
  return out;
}

/** Words for a catalogue row that cannot be priced at the chosen unit. */
function catalogueRefusalReason(
  quotationUsdToTtd: number | null,
  options: (CostUnitOption | GraphCostUnitOption)[],
  unit: string,
  /** Whether this row needs VAT and there is none set in Settings to gross it at. */
  needsVatRate: boolean
): string {
  if (quotationUsdToTtd == null || !Number.isFinite(quotationUsdToTtd) || quotationUsdToTtd <= 0) {
    return "This quotation's TT$ ↔ US$ rate has not loaded, so no catalogue price can be converted. Reload the page.";
  }
  if (options.length === 0) {
    // Do not guess (owner rule, 2026-09-24): a VAT-applicable row with no
    // Settings rate to gross at has no honest cost, and the refusal says so
    // rather than the generic "no price" — see `costPriceTiers.catalogueRowCostPrice`.
    if (needsVatRate) return 'VAT rate is not set in Settings, so this item\'s cost cannot be worked out.';
    return 'This row states no price to cost a line from.';
  }
  const canBe = options.map((o) => o.label).join(', ');
  return `This row cannot be priced per ${unit}. It can be priced at: ${canBe}.`;
}

/**
 * One row's price at `unit`, or why it has none.
 *
 * CATALOGUE — `catalogueCostUnitOptions` (this module's mirror of the
 * popup's own tier lookup), matched to `unit` by `isSameUnit`. No match is a
 * refusal naming what the row CAN be priced at, never the nearest tier.
 *
 * SUPPLIER — the row must already have been FETCHED for `unit`: the server is
 * the one owner of "what does this option cost per this unit"
 * (`supplierOptions.priceForLineUnit`), so this function only reads what it
 * already answered.
 *   1. `supplierOptionRefusal` first — it wins, and it is the same refusal
 *      `SupplierCell`'s dropdown shows today (QUO-00068).
 *   2. `lineUnitPrice` must be a real number — belt-and-braces once (1) has
 *      already passed, since a refusal-free option always has one.
 *   3. The option's own `lineUnit` must actually BE `unit` — a caller that
 *      hands this a supplier list fetched for a different unit (a stale array
 *      mid-refetch) is refused rather than shown a price silently mislabelled
 *      under the new unit's heading.
 *   4. Converted to US$ through `usdCostFacts` (fxDisplay, D85.6) — the ONE owner, never a second
 *      conversion written here. A currency it cannot convert (no rate, or a
 *      code that is neither TTD nor USD) is a refusal, never a guess.
 */
export function priceAtUnit(
  row: LookupRow,
  opts: {
    unit: string;
    quotationUsdToTtd: number | null;
    lineUom?: string | null;
    /** The Settings VAT rate (`getVatRate`), percent, or null. See `catalogueCostUnitOptions`. */
    vatRatePercent: number | null;
  }
): PriceAtUnitResult {
  const { unit, quotationUsdToTtd, vatRatePercent } = opts;

  if (row.kind === 'catalogue') {
    const item = row.item;
    const options = catalogueCostUnitOptions(item, quotationUsdToTtd, vatRatePercent);
    const match = options.find((o) => isSameUnit(o.uom, unit));
    if (!match) {
      // Reads the owner's own test (`catalogueRowNeedsUnsetVatRate`) for the
      // MESSAGE's wording — never a second, independently-derived boolean.
      const needsVatRate = catalogueRowNeedsUnsetVatRate(item, vatRatePercent);
      return {
        ok: false,
        reason: catalogueRefusalReason(quotationUsdToTtd, options, unit, needsVatRate),
        // See `noPrice`'s own doc comment: only "this row states no price at
        // all", never "not at this unit", "the rate has not loaded" or "the
        // Settings VAT rate is unset" (which also empties `options`).
        noPrice:
          !needsVatRate &&
          options.length === 0 &&
          quotationUsdToTtd != null &&
          Number.isFinite(quotationUsdToTtd) &&
          quotationUsdToTtd > 0,
      };
    }
    const selection: CataloguePriceSelection = {
      unitKey: match.key,
      label: match.label,
      unitPriceUsd: match.usd,
      unitPriceUsdExact: match.usdExact,
      unitPriceTtdWithVat: match.ttdWithVat,
      unitPriceTtdCost: match.ttdCost,
      uom: match.uom,
    };
    return {
      ok: true,
      usdExact: match.usdExact,
      usd: match.usd,
      ttdWithVat: match.ttdWithVat,
      ttdCost: match.ttdCost,
      uom: match.uom,
      catalogueSelection: selection,
    };
  }

  const option = row.option;
  const refusal = supplierOptionRefusal(option);
  if (refusal) return { ok: false, reason: refusal };

  if (typeof option.lineUnitPrice !== 'number' || !Number.isFinite(option.lineUnitPrice)) {
    return { ok: false, reason: 'This supplier price cannot be applied to this line.' };
  }

  // A stale array: this option still names the PREVIOUS chosen unit, from
  // before a unit-selector change kicked off a refetch. `ItemLookupPanel` sets
  // its loading state in the same tick as the unit change, so in practice this
  // is a one-frame window the person will only ever catch mid-click — worded
  // as the transient wait it is, never as a permanent refusal (it reads like
  // an internal state otherwise, and this is the one row a real person sees).
  if (option.lineUnit && unit && !isSameUnit(option.lineUnit, unit)) {
    return { ok: false, reason: `This supplier's price for ${unit} is still loading.` };
  }

  // `usdCostFacts` wants US$-per-TT$1 (`ttdToUsd`); `quotationUsdToTtd` is this
  // module's own TT$-per-US$1, the same convention `CataloguePriceSelection`
  // and the popup's `quotationUsdToTtd` prop use. One division between the
  // two conventions, never a rate re-derived from scratch.
  const ttdToUsd =
    quotationUsdToTtd != null && Number.isFinite(quotationUsdToTtd) && quotationUsdToTtd > 0
      ? 1 / quotationUsdToTtd
      : null;
  // D85.6: shown as the line will store it - a TT$ price ceiled once from the unrounded conversion.
  const costFacts = usdCostFacts(option.lineUnitPrice, option.currency, ttdToUsd);
  if (costFacts == null) {
    const stated = String(option.currency || '').trim().toUpperCase();
    return {
      ok: false,
      reason: `Priced in ${stated || 'a currency this row does not state'}. This quotation converts TT$ at its own locked rate and holds no rate for anything else, so this price cannot be applied to a line — it would go in as if it were US$.`,
    };
  }

  // The server now serves a supplier option's `unitPrice`/`lineUnitPrice`
  // VAT-INCLUSIVE — already the final cost USC pays, the same figure a
  // catalogue row's `ttdCost` now carries (owner rule, Deborah's VAT report,
  // 2026-09-24: input VAT is cost). Nothing HERE adds VAT a second time: `usd`
  // and `ttd` below are read straight off `option.lineUnitPrice`, through
  // `usdCostFacts`'s currency conversion only — no `withVat`, no `* 1.125`, on
  // this path at all. A directly-stated TT$ figure is reported as both
  // fields, honestly; a USD one reports neither rather than deriving a TT$
  // figure no source stated (the same refusal-over-guess rule as `ttdCost`
  // above it).
  const stated = String(option.currency || '').trim().toUpperCase();
  const ttd = stated === 'TTD' ? option.lineUnitPrice : null;

  return {
    ok: true,
    // D85.6: `usd` is the cost the line would store (a TT$ price ceiled once) and
    // `usdExact` the unrounded conversion beside it. A supplier pick is still WRITTEN
    // by the server from `option.unitPrice` + `option.currency` (the native, stated
    // fact — QUO-00054 is what anchoring off a rounded cent costs); these two only
    // say what the pick will read.
    usdExact: costFacts.unitPriceExact,
    usd: costFacts.unitPrice,
    ttdWithVat: ttd,
    ttdCost: ttd,
    uom: option.lineUnit || unit,
  };
}

/**
 * The merged list, best name match first (owner decision D109.10, E2E QA L4:
 * "White" listed sugar above white rice, and a hand-entered row showed twice).
 *
 * Order: RELEVANCE tier first, then price inside a tier.
 *   tier 0  the row's name is exactly the search words (case/punctuation ignored)
 *   tier 1  the name contains EVERY search word
 *   tier 2  everything else the server returned (it ORs the words)
 * Inside a tier, cheapest first at the chosen unit: a catalogue row and a
 * supplier row sort against each other on the SAME US$ figure at the SAME unit.
 * A blank search puts every row in tier 2, so it is the old cheapest-first.
 *
 * Rows that cannot be priced sort LAST inside their tier, in their original
 * order - never hidden (a hidden row reads as "this supplier does not have the
 * item") and never guessed into a position a real price would earn.
 *
 * A supplier row that IS a catalogue row already in the list (its
 * `masterCatalogueId` equals a catalogue row's `_id`: hand-entered catalogue
 * rows are served by both sources) is dropped. Any other supplier row stays -
 * one row per vendor + pack is by design.
 *
 * Words: lower-cased runs of letters/digits. No product-name tokeniser exists
 * client-side (the server's lives in `utils/productMatching.js` and strips
 * sizes, which a search must keep), so this is the one split used here.
 *
 * `pricedBy` is `priceAtUnit` with its options already closed over, so a test
 * can hand this a canned answer per row. `Array.prototype.sort` is stable
 * (ES2019), so equal keys keep their original relative order.
 */
function searchWords(text: unknown): string[] {
  return String(text ?? '')
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
}

function rowName(row: LookupRow): string {
  return row.kind === 'catalogue' ? row.item.item_name : row.option.description;
}

/**
 * A number in the search ("500") is present in a name that writes it with its unit ("500ml"): the
 * name's word is that number followed by letters. Never a prefix of a longer number ("5000ml"), and
 * only a number gets this, so a word like "co" does not match "cola" (B012).
 */
function sizeWordPresent(queryWord: string, nameWords: string[]): boolean {
  if (!/^\d+$/.test(queryWord)) return false;
  return nameWords.some((w) => w.startsWith(queryWord) && /^\p{L}/u.test(w.slice(queryWord.length)));
}

function relevanceTier(row: LookupRow, queryWords: string[]): 0 | 1 | 2 {
  if (queryWords.length === 0) return 2;
  const words = searchWords(rowName(row));
  if (words.length === queryWords.length && words.every((w, i) => w === queryWords[i])) return 0;
  const have = new Set(words);
  return queryWords.every((w) => have.has(w) || sizeWordPresent(w, words)) ? 1 : 2;
}

export function sortLookupRows(
  rows: LookupRow[],
  pricedBy: (row: LookupRow) => PriceAtUnitResult,
  searchText?: string
): LookupRow[] {
  const catalogueIds = new Set<string>();
  for (const row of rows) if (row.kind === 'catalogue') catalogueIds.add(String(row.item._id));
  const queryWords = searchWords(searchText);

  const ranked: { row: LookupRow; tier: number; usd: number | null }[] = [];
  for (const row of rows) {
    if (row.kind === 'supplier') {
      const mcId = row.option.masterCatalogueId;
      if (mcId != null && catalogueIds.has(String(mcId))) continue;
    }
    const result = pricedBy(row);
    ranked.push({ row, tier: relevanceTier(row, queryWords), usd: result.ok ? result.usd : null });
  }
  ranked.sort((a, b) => {
    if (a.tier !== b.tier) return a.tier - b.tier;
    if (a.usd == null || b.usd == null) return a.usd == null ? (b.usd == null ? 0 : 1) : -1;
    return a.usd - b.usd;
  });
  return ranked.map((r) => r.row);
}

/**
 * WHICH ROW A QUOTATION LINE WAS ACTUALLY PRICED FROM — so the panel can
 * highlight that ONE row, never every row that happens to share a name.
 *
 * Owner, 2026-09-24: `isSelected` used to be `vendorName === selectedVendor`,
 * so three catalogue/supplier rows from the same real-world supplier (three
 * TROPICANA rows, say) all lit up at once, whichever one actually priced the
 * line. A vendor's NAME is not a row's identity — several rows genuinely
 * share it — so this reads the line's own `pricingDetails` for the one fact
 * that names a SPECIFIC row instead.
 *
 *   - CATALOGUE: `pricingDetails.masterCatalogueId` — a real Mongo `_id`, set
 *     by a catalogue pick (`cataloguePickDetails.pricingDetailsForCataloguePick`,
 *     this same panel's OTHER tab) and by generation's own Master Catalogue
 *     match (`quotationGeneration.service.js`). The same field
 *     `CatalogueOptionsLink.recordsCatalogueRow` already reads for the
 *     "Catalogue ↗" chip's own highlight, so this is not a second reading of
 *     it — just the same fact asked here too.
 *   - SUPPLIER: `pricingDetails.originalItem` + `pricingDetails.originalItemVendor`
 *     — the matched document's own vendor + description, the SAME
 *     `vendorName|description` identity `supplierOptions.optionKey` resolves a
 *     pick by (`utils/supplierOptionIdentity`, one owner). `originalItem`
 *     alone is generation's own reference fact (`bestMatch.item_description_raw`
 *     / `.description`, off the exact field `supplierOptions.service.js`
 *     states as a row's `description`) and is ALSO every OTHER vendor's
 *     identically-worded row — three suppliers can all print "EGGS WHITE 30S"
 *     — the multi-highlight bug an earlier version of this function had
 *     (owner audit, 2026-09-25: matching on description alone). Both keys are
 *     REFERENCE FACTS (`quotationLinePricingFacts.REFERENCE_FACTS`) that
 *     survive specifically for this purpose; every writer that replaces a
 *     line's price clears them first, so they can never outlive the price
 *     they described.
 *
 * A LISTED-option pick from THIS panel (`quotationLineSupplierPrice
 * .factsForListedOption`) now sets BOTH fields (owner audit, 2026-09-25) — it
 * used to set neither, so such a line carried no identifiable source once
 * picked and nothing ever highlighted after it. `originalItemVendor` is new:
 * generation never sets it, so a GENERATION match — a description with no
 * vendor recorded beside it — answers null here rather than guessing among
 * every row sharing that description, the same refusal-over-guess rule this
 * module has always followed. A `HistoricalQuotationItem` row is a further
 * known gap: `pricingDetails.fileName` (its own reference fact) names the
 * PARENT document, not the SAME field the live row's `vendorName` reads
 * (`item.source`, a different per-row string) — see
 * `supplierOptionsHistoricalVendorName.test.js`'s own header, which already
 * logs this as pre-existing tech debt rather than guessed at here.
 */
export interface PricedFromIdentity {
  /** A catalogue row's `_id`, or null when the line was not priced from one. */
  masterCatalogueId: string | null;
  /**
   * A supplier row's own stated description, or null when the line carries
   * none — either because nothing recorded one, or because a later write
   * (any pick that replaces the price) retired it along with the price it
   * described.
   */
  originalItemDescription: string | null;
  /**
   * The vendor beside `originalItemDescription` — null on the same terms
   * (including a GENERATION match, which records the description alone).
   */
  originalItemVendor: string | null;
  /**
   * True when the line's CURRENT price came from a LISTED-option pick — the
   * mirrored owner `quotationLinePricingFacts.pickedAsSupplierRow`, read off
   * `pricingDetails.supplierDropdownPick`. Used only to break a tie: a
   * hand-entered Master Catalogue row is BOTH a catalogue row and a supplier
   * option in this merged panel, and picking it as the SUPPLIER row still
   * re-seeds `masterCatalogueId` (so the "Catalogue ↗" link points at the
   * right row) — without this flag, that SAME document's catalogue-kind row
   * would light up too. The row kind the user actually clicked wins.
   */
  pickedAsSupplierRow: boolean;
}

export function pricedFromIdentity(pricingDetails: unknown): PricedFromIdentity {
  const details =
    pricingDetails && typeof pricingDetails === 'object' && !Array.isArray(pricingDetails)
      ? (pricingDetails as Record<string, unknown>)
      : {};
  const mcId = details.masterCatalogueId;
  const original = details.originalItem;
  const vendor = details.originalItemVendor;
  return {
    masterCatalogueId: mcId != null && String(mcId).trim() ? String(mcId).trim() : null,
    originalItemDescription:
      typeof original === 'string' && original.trim() ? original.trim() : null,
    originalItemVendor: typeof vendor === 'string' && vendor.trim() ? vendor.trim() : null,
    pickedAsSupplierRow: pickedAsSupplierRow(pricingDetails),
  };
}

/**
 * Whether THIS row is the one `identity` names.
 *
 * A catalogue row matches on its own `_id` — exact, and it can only ever name
 * one row — UNLESS `identity.pickedAsSupplierRow` says the current price came
 * from picking this same document as a SUPPLIER row instead (see
 * `PricedFromIdentity.pickedAsSupplierRow`'s own header): then the
 * catalogue-kind twin stays dark and only the supplier-kind row below lights.
 *
 * A supplier row matches on its own stated vendor AND description together —
 * `supplierOptionIdentityKey`, the identity `supplierOptions.service.js`
 * resolves a pick by — never on description alone, which is the fallback this
 * function exists to refuse (`PricedFromIdentity`'s own header explains why).
 */
export function isPricedRow(row: LookupRow, identity: PricedFromIdentity): boolean {
  if (row.kind === 'catalogue') {
    if (identity.pickedAsSupplierRow) return false;
    return identity.masterCatalogueId != null && String(row.item._id) === identity.masterCatalogueId;
  }
  if (identity.originalItemVendor == null || identity.originalItemDescription == null) return false;
  return (
    supplierOptionIdentityKey({ vendorName: row.option.vendorName, description: row.option.description }) ===
    supplierOptionIdentityKey({ vendorName: identity.originalItemVendor, description: identity.originalItemDescription })
  );
}
